import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Select } from '@any-ui-react/select';
import { Lens } from '@hookform/lenses';

import { CrmUrlInput } from '@anyx/feature-crm/shared';


import { useBroadcastTrackingLink, useVariableUrlInput } from '../hooks';
import { useLineMessageStore } from '../store';
import { UrlActionType } from '../types';

const UrlActionSelect = ({ lens }: { lens: Lens<UrlActionType> }) => {
  const { t } = useTranslation('crm');
  const { field } = useController(lens.interop());

  return (
    <Select
      {...field}
      label={t('crm.lineMessageForm.fields.urlAction.select.label')}
      onChange={(e) => field.onChange(e?.value)}
      options={[
        {
          label: t('crm.lineMessageForm.fields.urlAction.select.options.manual'),
          value: UrlActionType.enum.MANUAL,
        },
        {
          label: t('crm.lineMessageForm.fields.urlAction.select.options.variable'),
          value: UrlActionType.enum.VARIABLE,
        },
      ]}
    />
  );
};

export const UrlActionManualInput = ({
  lens,
  trackingEnabled,
  required,
}: {
  lens: Lens<string>;
  trackingEnabled?: boolean;
  required?: boolean;
}) => {
  const { t } = useTranslation('crm');
  const { field, fieldState } = useController(lens.interop());
  const { findTrackingLink, insertTrackingLink } = useBroadcastTrackingLink();
  const isFormDisabled = useLineMessageStore((store) => store.isFormDisabled);

  return (
    <CrmUrlInput
      {...field}
      required={required}
      withTrackingLink={trackingEnabled}
      onConvertToTrackingLink={insertTrackingLink}
      onChange={(e) => {
        field.onChange(e.displayUrl || e.input);
      }}
      value={findTrackingLink(field.value)}
      label={t('crm.lineMessageForm.fields.url.label')}
      placeholder={t('crm.lineMessageForm.fields.url.placeholder')}
      error={fieldState.error?.message}
      readOnly={isFormDisabled}
    />
  );
};

export const UrlActionVariableSelect = ({
  lens,
  required,
}: {
  lens: Lens<string>;
  required?: boolean;
}) => {
  const { t } = useTranslation('crm');
  const { field, fieldState } = useController(lens.interop());

  const { variableUrlOptions } = useVariableUrlInput();

  return (
    <Select
      {...field}
      error={fieldState.error?.message}
      required={required}
      onChange={(e) => {
        field.onChange(e?.value);
      }}
      label={t('crm.lineMessageForm.fields.urlAction.variable.label')}
      options={variableUrlOptions}
    />
  );
};

export const UrlActionBlock = ({
  lens,
  trackingEnabled,
  actionStatus,
  required,
}: {
  lens: Lens<{ status: UrlActionType; manual: string; variable: string }>;
  trackingEnabled?: boolean;
  actionStatus: UrlActionType;
  required?: boolean;
}) => {
  const { enableVariableUrl } = useVariableUrlInput();

  return (
    <>
      {enableVariableUrl && <UrlActionSelect lens={lens.focus('status')} />}
      {actionStatus === UrlActionType.enum.MANUAL && (
        <UrlActionManualInput
          required={required}
          lens={lens.focus('manual')}
          trackingEnabled={trackingEnabled}
        />
      )}
      {actionStatus === UrlActionType.enum.VARIABLE && (
        <UrlActionVariableSelect required={required} lens={lens.focus('variable')} />
      )}
    </>
  );
};
